import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import Form from '../../components/Fields/Form';
import SwitchField from '../../components/Fields/SwitchField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import CountryModel from '../../models/CountryModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { DataResult } from '../../types/Result';
import Unauthorized from '../Authentication/Unauthorized';
import Country_tab_Regions from './Tabs/Country_tab_Regions';

const CountryView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<CountryModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<CountryModel>({
        id: 0,
        code: '',
        name: '',
        isActive: true,
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let countryId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        countryId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (countryId !== undefined) {
            DataService.getById<CountryModel>('api/v1/countries/', countryId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [countryId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<CountryModel>(`api/v1/countries/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<CountryModel>(`api/v1/countries/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'code', label: 'Code' },
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'isActive', label: 'Active' },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (countryId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditCountry)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Country' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditCountry}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('code')} />
                            <TextboxField {...getFieldProps('name')} />
                            <SwitchField {...getFieldProps('isActive')} />
                        </Col>
                    </Row>
                </Container>
            </Form>
            {countryId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='regions'
                                tabs={[
                                    {
                                        title: 'Regions',
                                        eventKey: 'regions',
                                        component: <Country_tab_Regions id={countryId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default CountryView;